import View                      from '@widesk-ui/components/View';
import Text                      from '@widesk-ui/components/Text';
import { observer }              from 'mobx-react-lite';
import currentProjectEditorStore from '@/TokamapEditor/stores/currentProjectEditorStore';
import Button                    from '@widesk-ui/components/Button';
import EditableText              from '@widesk-ui/components/form/EditableText';
import Tooltip                   from '@widesk-ui/components/Tooltip';
import Avatar                     from '@widesk-ui/Avatar';
import { AvatarGroup }            from '@widesk-ui/Avatar';
import { lastModificationLabel }  from '@/TokamapEditor/ux/lastModification';
import { userName }               from '@/TokamapEditor/ux/lastModification';
import currentEditorStore         from '@/TokamapEditor/stores/currentEditorStore';
import { DeleteOutlined }        from '@ant-design/icons';
import { PlusOutlined }          from '@ant-design/icons';
import { EnvironmentOutlined }   from '@ant-design/icons';
import ModalTitle                from '@/components/ModalTitle';
import React                     from 'react';
import ButtonActions             from '@widesk-ui/components/ButtonActions';
import { deleteModelConfirm }    from '@widesk-ui/tools/actionModel/actionDeleteModel';
import MapForm                   from '@forms/map/MapForm';
import { FormModal }             from '@widesk-ui/components/modal/FormModal';
import MapModel                  from '@/models/services/map/MapModel';
import FunctionalityGate         from '@/components/Functionality';
import useDesignTokens           from '@/hooks/useDesignTokens';
import { SwitcherMenu }          from '@/TokamapEditor/ux/SwitcherMenu';
import { SwitcherMenuRow }       from '@/TokamapEditor/ux/SwitcherMenu';
import { SwitcherMenuCheck }     from '@/TokamapEditor/ux/SwitcherMenu';
import { refreshMapUsages }      from '@/components/Functionality/refreshMapUsages';
import compareMapsByPosition     from '@/TokamapEditor/helpers/compareMapsByPosition';
import { HolderOutlined }        from '@ant-design/icons';
import { message }               from '@widesk-ui/hooks/useMessage';
import messageWithUndo           from '@/TokamapEditor/helpers/messageWithUndo';
import { DndContext }            from '@dnd-kit/core';
import { PointerSensor }         from '@dnd-kit/core';
import { KeyboardSensor }        from '@dnd-kit/core';
import { useSensor }             from '@dnd-kit/core';
import { useSensors }            from '@dnd-kit/core';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { restrictToParentElement } from '@dnd-kit/modifiers';
import { SortableContext }       from '@dnd-kit/sortable';
import { useSortable }           from '@dnd-kit/sortable';
import { arrayMove }             from '@dnd-kit/sortable';
import { sortableKeyboardCoordinates } from '@dnd-kit/sortable';
import { verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS }                   from '@dnd-kit/utilities';

const ellipsis: React.CSSProperties = {
	whiteSpace  : 'nowrap',
	overflow    : 'hidden',
	textOverflow: 'ellipsis',
};

const MapMenu = observer(() => {
	const projectEditor = currentProjectEditorStore.get;
	const tokens = useDesignTokens();
	const activeRef = React.useRef<HTMLDivElement | null>(null);
	const [search, setSearch] = React.useState<string>();

	React.useEffect(() => {
		activeRef.current?.scrollIntoView({ behavior: 'auto', block: 'nearest' });
	}, []);

	const maps = [...projectEditor.mapCollection.models].sort(compareMapsByPosition);

	const filteredMaps = search
		? maps.filter(m => m.label?.toLowerCase().includes(search.toLowerCase()))
		: maps;

	const sortable = !search && maps.length > 1;

	const sensors = useSensors(
		useSensor(PointerSensor),
		useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
	);

	return (
		<SwitcherMenu
			title="Vos cartes"
			count={maps.length}
			searchPlaceholder="Rechercher une carte…"
			onSearch={setSearch}
			extra={<BtnAdd />}
		>
			<DndContext
				sensors={sensors}
				modifiers={[restrictToVerticalAxis, restrictToParentElement]}
				onDragEnd={({ active, over }) => {
					if (!over || active.id === over.id) return;

					const ids = maps.map(m => String(m.id));
					const next = arrayMove(maps, ids.indexOf(String(active.id)), ids.indexOf(String(over.id)));

					projectEditor.reorderMaps(next)
						.then(() => messageWithUndo(
							<span>L'ordre des cartes a été enregistré.</span>,
							() => projectEditor.reorderMaps(maps),
						))
						.catch(err => message.exception(err));
				}}
			>
				<SortableContext items={filteredMaps.map(m => String(m.id))} strategy={verticalListSortingStrategy}>
					<View gap={2}>
						{filteredMaps.map(m => (
							<SortableMapRow
								key={m.id}
								map={m}
								sortable={sortable}
								active={projectEditor.currentMap.id === m.id}
								innerRef={projectEditor.currentMap.id === m.id ? activeRef : undefined}
							/>
						))}
					</View>
				</SortableContext>
			</DndContext>

			{filteredMaps.length === 0 && (
				<View p="sm" center>
					<Text color={tokens.text.muted}>Aucune carte ne correspond à « {search} »</Text>
				</View>
			)}
		</SwitcherMenu>
	);
});

type SortHandle = Pick<ReturnType<typeof useSortable>, 'listeners' | 'setActivatorNodeRef'>;

const SortableMapRow = observer((props: {
	map: MapModel;
	sortable: boolean;
	active: boolean;
	innerRef?: React.Ref<HTMLDivElement>;
}) => {
	const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } = useSortable({
		id: String(props.map.id),
		disabled: !props.sortable,
	});

	return (
		<div
			ref={setNodeRef}
			{...attributes}
			style={{
				transform: CSS.Translate.toString(transform),
				transition,
				...(isDragging ? { position: 'relative', zIndex: 1, opacity: 0.85 } : {}),
			}}
		>
			<MapRow
				map={props.map}
				active={props.active}
				innerRef={props.innerRef}
				handle={props.sortable ? { listeners, setActivatorNodeRef } : undefined}
				dragging={isDragging}
			/>
		</div>
	);
});

const MapRow = observer((props: {
	map: MapModel;
	active: boolean;
	innerRef?: React.Ref<HTMLDivElement>;
	handle?: SortHandle;
	dragging?: boolean;
}) => {
	const { map, active, innerRef, handle } = props;
	const projectEditor = currentProjectEditorStore.get;
	const [hovered, setHovered] = React.useState(false);
	const tokens = useDesignTokens();
	const toolsVisible = hovered || !!props.dragging;

	return (
		<SwitcherMenuRow
			active={active}
			innerRef={innerRef}
			onHover={setHovered}
			onClick={() => projectEditor.setCurrentMap(map)}
		>
			<View flex centerV style={{ minWidth: 0 }}>
				{/* Réserve la place du menu `⋯`, qui est hors flux (voir plus bas) : seul le titre peut
				    l'atteindre, les lignes en dessous vont jusqu'au bord. */}
				<View row gap="xs" centerV pr={handle ? 52 : 26}>
					{active && <SwitcherMenuCheck />}
					<View bold style={{ minWidth: 0 }}>
						<EditableText
							value={map.label}
							emptyLabel="Sans titre"
							size="small"
							editTrigger="icon"
							onSubmit={async value => {
								if (!value) throw new Error('Le titre ne peut pas être vide.');

								await map.patch({ label: value });
							}}
						/>
					</View>
				</View>

				<View row centerV gap="xs">
					<View flex style={ellipsis}>
						<MapLastModification map={map} />
					</View>

					<MapUsers map={map} />
				</View>
			</View>

			<View
				row
				centerV
				onClick={e => e.stopPropagation()}
				style={{
					position: 'absolute',
					top: 4,
					right: 4,
					opacity: toolsVisible ? 1 : 0,
					transition: 'opacity .15s ease',
				}}
			>
				{handle && (
					<div
						ref={handle.setActivatorNodeRef}
						{...handle.listeners}
						aria-label="Déplacer la carte"
						style={{
							width: 26,
							height: 26,
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'center',
							cursor: 'grab',
							touchAction: 'none',
							color: tokens.text.muted,
						}}
					>
						<HolderOutlined />
					</div>
				)}

				<ButtonActions
					type="text"
					size="small"
					style={{ width: 26, height: 26, padding: 0 }}
					actions={[
						{
							icon: <DeleteOutlined />,
							label: 'Supprimer',
							danger: true,
							disabled: active,
							onClick: () => {
								deleteModelConfirm(map, {
									title: `Supprimer`,
									content: `Voulez-vous supprimer la carte "${map.label}" ? Cette action est irréversible`,
									callback: () => void refreshMapUsages(),
								});
							},
						},
					]}
				/>
			</View>
		</SwitcherMenuRow>
	);
});

/**
 * Dernière modification de CETTE carte — la même donnée que le message de la barre du haut (qui en
 * affiche, lui, le maximum du projet), donc tenue à jour en direct par Mercure comme lui.
 *
 * Remplace le `By` d'origine, qui affichait la date de CRÉATION de la carte : une information figée,
 * et pas celle qu'on cherche en choisissant une carte dans cette liste.
 */
const MapLastModification = observer((props: { map: MapModel; }) => {
	const tokens = useDesignTokens();
	const lastModification = currentProjectEditorStore.get?.getLastModification(props.map.urn);

	if (!lastModification) return null;

	const who = lastModification.byUserUrn ? userName(lastModification.byUserUrn) : undefined;

	return (
		<Text size={11} color={tokens.text.muted}>
			{lastModificationLabel(lastModification.at)}{who ? ` par ${who}` : ''}
		</Text>
	);
});

/**
 * Qui travaille en ce moment sur cette carte. Vient de la présence à l'échelle du projet
 * (`server:project-users`) : sans elle, on ne connaîtrait que les personnes de la carte ouverte, et
 * cette colonne serait vide partout ailleurs — c'est-à-dire exactement là où elle est utile.
 */
const MapUsers = observer((props: { map: MapModel; }) => {
	const users = currentEditorStore.editorUserStore.projectUsers
		.filter(user => String(user.mapId) === String(props.map.id));

	if (!users.length) return null;

	return (
		<View row onClick={e => e.stopPropagation()}>
			{/* Taille explicite plutôt que `size="small"` (24px) : ces avatars accompagnent un texte de
			    11px, ils doivent rester en dessous de lui en importance. La police des initiales est
			    posée à la main, AntD ne l'ajustant qu'à ses tailles nommées. */}
			<AvatarGroup size={22} max={{ count: 3 }}>
				{users.map(user => (
					<View key={user.id}>
						<Tooltip title={`${user.firstName} ${user.lastName}`} placement="top">
							<Avatar size={22} style={{ backgroundColor: user.color, fontSize: 10 }}>
								{[user.lastName, user.firstName].map(str => str.at(0)).join('')}
							</Avatar>
						</Tooltip>
					</View>
				))}
			</AvatarGroup>
		</View>
	);
});

const BtnAdd = observer(() => {
	const projectEditor = currentProjectEditorStore.get;

	// `fullWidth` : le bouton est `block`, sa largeur de 100% se résout contre l'enveloppe du gate.
	// Sans lui, celle-ci est un `inline-flex` qui s'ajuste à son contenu et le bouton cesse de remplir
	// la colonne dès que la limite est atteinte.
	return (
		<FunctionalityGate reference="map_limit" context={projectEditor.project} fullWidth>
			<Button
				type="primary"
				block
				icon={<PlusOutlined />}
				onClick={() => {
					const newMap = new MapModel();

					FormModal.open({
						children: (
							<MapForm
								model={newMap}
								projectUrn={projectEditor.project.urn}
								onSuccess={() => {
									projectEditor.mapCollection.add(newMap);
									projectEditor.setCurrentMap(newMap);
									void refreshMapUsages();
								}}
							/>
						),
						// Même icône que le segment « Vos cartes » de la barre flottante (`MapButton`) : on
						// reconnaît ce dont il est question avant d'avoir lu le titre.
						title: <ModalTitle icon={<EnvironmentOutlined />}>Ajouter {MapModel.localize('a')}</ModalTitle>,
					});
				}}
			>
				Nouvelle carte
			</Button>
		</FunctionalityGate>
	);
});

export default MapMenu;
